import { FC } from 'react'
import { tokens } from '@/theme'
import { ETAPAS_COMERCIAIS, ETAPA_COMERCIAL_LABEL, EtapaComercial } from '@/types/statusReserva'
import { formatBRL, formatBRLCurto } from './canais'
import { GraficoColunas } from './GraficoColunas'
import { LegendaGrafico, LegendaGraficoProps } from './LegendaGrafico'

/**
 * Cor de cada etapa na identidade Originei: rampa do laranja (tint claro na Reserva ao
 * laranja escuro na Negociação), Ganho em Ink, Perdido apagado. A mesma rampa das
 * faixas dos cards do Funil por canal (Visão de Carteira).
 */
export const COR_ETAPA_MARCA: Record<EtapaComercial, string> = {
  RESERVA: '#F9B79A',
  REUNIAO: tokens.brandPrimaryLight,
  ESTRUTURACAO: tokens.brandPrimary,
  NEGOCIACAO: tokens.brandPrimaryDark,
  GANHO: tokens.brandInk,
  PERDIDO: '#C4C9D1',
}

export interface FunilEtapasProps {
  /** `pipeline.porEtapaComercial` do resumo. */
  porEtapa: Record<string, number> | undefined
  /**
   * `pipeline.valorPorEtapaComercial` do resumo (R$ sob cada etapa). Opcional: sem ele
   * o funil mostra só a contagem (o consultor vê o R$ no card "Volume por etapa" — VC-01).
   */
  valorPorEtapa?: Record<string, number>
  /** Clique numa etapa (ex.: abre o Kanban). */
  aoClicar?: (etapa: EtapaComercial) => void
  altura?: number
  /**
   * Legenda (L13): unidade, período e critério. Opcional — o Dashboard do consultor passa;
   * as outras telas que reusam o funil seguem como estavam.
   */
  legenda?: LegendaGraficoProps
}

/**
 * Funil em seis etapas comerciais como colunas verticais (conceito BI, 11/09) — a
 * contagem em cima, o R$ abreviado embaixo (só quando `valorPorEtapa` vem). Os números vêm prontos do
 * `/v1/dashboard/resumo`; nada é somado aqui.
 */
export const FunilEtapas: FC<FunilEtapasProps> = ({
  porEtapa,
  valorPorEtapa,
  aoClicar,
  altura,
  legenda,
}) => (
  <>
    <GraficoColunas
      altura={altura}
      aoClicar={aoClicar ? (chave) => aoClicar(chave as EtapaComercial) : undefined}
      itens={ETAPAS_COMERCIAIS.map((etapa) => {
        const contagem = porEtapa?.[etapa] ?? 0
        const base = {
          chave: etapa,
          rotulo: ETAPA_COMERCIAL_LABEL[etapa],
          valor: contagem,
          cor: COR_ETAPA_MARCA[etapa],
        }
        if (!valorPorEtapa) return base
        const valor = Number(valorPorEtapa[etapa] ?? 0)
        return {
          ...base,
          detalhe: formatBRLCurto(valor),
          tooltip: `${ETAPA_COMERCIAL_LABEL[etapa]}: ${contagem} · ${formatBRL(valor)}`,
        }
      })}
    />
    {legenda && <LegendaGrafico {...legenda} />}
  </>
)

export default FunilEtapas
